// Products tab — customs details per client SKU.
//
// One component for both portals:
//   mode="admin"   admin ClientDetail. Only shown to logins in
//                  PRODUCTS_FEATURE_EMAILS while the feature is in testing.
//                  Adds Import from ShipStation, client-access switch,
//                  remove/restore, change history, upload note and the
//                  "where it came from" column. "Preview client view" draws the
//                  client presentation but keeps using the admin API, so
//                  anything saved from the preview is recorded as OPS.
//   mode="client"  client Account page. Only shown once the client's access is
//                  switched on (or to a feature-allowed admin as a preview).
//
// The sync worker adds a new SKU within about 5 minutes of its first order
// reaching ShipStation, so an open tab refreshes its list every 5 minutes and
// when you come back to the browser tab.
//
// Field rules mirror backend/services/products.py; the server re-checks
// everything, so these checks are for instant feedback only. Whether a product
// is complete comes from the server (`complete` / `missing` on each row).

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Loader2, Upload, Download, RefreshCw, Search, Lock, Eye, Trash2, Info, CheckCircle2, AlertCircle, X,
  RotateCcw, ArrowLeft, History as HistoryIcon,
} from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import {
  getProducts, updateProduct, uploadProducts, confirmProducts, deleteProduct, restoreProduct,
  getProductHistory, importProductsFromShipstation, setProductsClientAccess,
} from '../lib/api'
import { COUNTRIES, COUNTRY_ALIASES } from '../lib/customsCountries'
import { ToastContainer, useToast } from './Toast'

// ─── Small helpers ────────────────────────────────────────────────────────────

function asText(v) { return v === null || v === undefined ? '' : String(v) }

function fmtDate(iso, pattern = 'd MMM yyyy') {
  if (!iso) return ''
  const d = parseISO(String(iso))
  return isValid(d) ? format(d, pattern) : ''
}

// A problem found in the browser (a bad file or header), shown as written.
function problem(message) {
  const e = new Error(message)
  e.userMessage = message
  return e
}

// The one way any failure becomes a sentence. FastAPI's `detail` can be a
// string, an object like {field, message}, or (422) an array of {loc, msg}.
// Never put an object into JSX: React throws and the page goes blank.
function errorMessage(err, fallback) {
  if (typeof err?.userMessage === 'string' && err.userMessage) return err.userMessage
  const d = err?.response?.data?.detail
  if (typeof d === 'string' && d.trim()) return d.trim()
  if (Array.isArray(d)) {
    const msgs = d
      .map(x => (typeof x === 'string' ? x : typeof x?.msg === 'string' ? x.msg : typeof x?.message === 'string' ? x.message : ''))
      .filter(Boolean)
    if (msgs.length) return msgs.join('; ')
  } else if (d && typeof d === 'object' && typeof d.message === 'string' && d.message.trim()) {
    return d.message.trim()
  }
  return fallback
}

// ─── Field rules ──────────────────────────────────────────────────────────────

// What people type (or Excel writes) when they don't know a value. Rejected in
// typed text so a product can't look complete when it isn't. Same list as the
// server, which doesn't re-check stored values against it ("NA" is Namibia).
const PLACEHOLDERS = new Set([
  'n/a', 'na', 'n.a.', 'n.a', 'tbc', 'tba', 'tbd', '-', '--', '---', '?', '??', 'none', 'null', 'nil', 'unknown',
  '#n/a', '#value!', '#ref!', '#name?', '#div/0!', '#null!', '#num!', '#spill!', '#calc!',
])
const PLACEHOLDER_MSG = 'This looks like a placeholder. Leave the cell blank instead.'
function isPlaceholder(v) { return PLACEHOLDERS.has(asText(v).trim().toLowerCase()) }

// Countries. Typed names resolve the same way as backend resolve_country.
const COUNTRY_NAME = Object.fromEntries(COUNTRIES)
const NAMIBIA_HINT = 'If you mean Namibia, pick it from the list.'

// Lower case, curly → straight apostrophe, & → and, single spaces, no
// leading/trailing dots or spaces.
function countryKey(s) {
  return asText(s).toLowerCase().replace(/[’‘ʼ]/g, "'").replace(/&/g, 'and').replace(/\s+/g, ' ').replace(/^[ .]+|[ .]+$/g, '')
}

// Everyday spellings of an official name, as backend _name_variants:
// "Hong Kong SAR China" → "hong kong", "Myanmar (Burma)" → "myanmar" / "burma",
// "St. Lucia" → "saint lucia" / "st lucia".
function nameVariants(name) {
  const base = countryKey(name)
  const out = new Set([base])
  const m = base.match(/^(.*?)\s*\((.*)\)$/)
  if (m) { out.add(m[1]); out.add(m[2]) }
  for (const v of [...out]) {
    out.add(v.replace(/\s+sar\s+china$/, ''))
    if (v.startsWith('st. ')) { out.add(`saint ${v.slice(4)}`); out.add(`st ${v.slice(4)}`) }
  }
  return [...out].map(v => v.trim()).filter(Boolean)
}

const COUNTRY_SEARCH = COUNTRIES.map(([code, name]) => ({ code, name, keys: nameVariants(name) }))
const COUNTRY_BY_NAME = {}
for (const c of COUNTRY_SEARCH) for (const k of c.keys) COUNTRY_BY_NAME[k] = c.code
const ALIAS_LIST = Object.entries(COUNTRY_ALIASES)
  .map(([k, code]) => [countryKey(k), code])
  .filter(([, code]) => COUNTRY_NAME[code])
const ALIAS = Object.fromEntries(ALIAS_LIST)

// A typed "NA" or "N/A" is almost always a placeholder, never Namibia.
function isNamibiaPlaceholder(q) { return ['na', 'n/a'].includes(asText(q).trim().toLowerCase()) }

// Suggestions for what's been typed. Nothing for an empty query, so Enter in
// an untouched cell can't pick the first country in the list.
function countryMatches(q) {
  const k = countryKey(q)
  if (!k) return []
  if (isNamibiaPlaceholder(q)) return [{ code: 'NA', name: COUNTRY_NAME.NA }]
  const hits = []
  const add = (code) => { if (COUNTRY_NAME[code] && !hits.some(h => h.code === code)) hits.push({ code, name: COUNTRY_NAME[code] }) }
  if (ALIAS[k]) add(ALIAS[k])
  if (COUNTRY_BY_NAME[k]) add(COUNTRY_BY_NAME[k])
  if (/^[a-z]{2}$/.test(k)) add(k.toUpperCase())
  for (const c of COUNTRY_SEARCH) if (c.keys.some(v => v.startsWith(k))) add(c.code)
  for (const [a, code] of ALIAS_LIST) if (a.startsWith(k)) add(code)
  for (const c of COUNTRY_SEARCH) if (c.keys.some(v => v.includes(k))) add(c.code)
  return hits.slice(0, 8)
}

// What typed text resolves to when the cell is left: { code } ('' clears the
// cell) or { error }.
function resolveTypedCountry(text) {
  const t = asText(text).trim()
  if (!t) return { code: '' }
  if (isNamibiaPlaceholder(t)) return { error: NAMIBIA_HINT }
  if (isPlaceholder(t)) return { error: PLACEHOLDER_MSG }
  if (/^[A-Za-z]{2}$/.test(t) && COUNTRY_NAME[t.toUpperCase()]) return { code: t.toUpperCase() }
  const k = countryKey(t)
  const code = ALIAS[k] || COUNTRY_BY_NAME[k]
  return code ? { code } : { error: `“${t}” isn’t a country we recognise. Type the name and pick it from the list.` }
}

// What goes to the API for a picked country. The server rejects a bare "NA"
// as a placeholder, so Namibia is sent by name; it's stored as NA.
function countryForApi(code) { return code === 'NA' ? 'Namibia' : code }

const STOP_WORDS = new Set([
  'gift', 'gifts', 'sample', 'samples', 'part', 'parts', 'accessory', 'accessories', 'goods',
  'general goods', 'merchandise', 'misc', 'miscellaneous', 'other', 'others', 'n/a', 'na', 'none',
  'unknown', 'stuff', 'item', 'items', 'product', 'products', 'clothing', 'clothes', 'apparel',
  'personal effects', 'various',
])

// "1,250.50" → "1250.50"; "12,50" (decimal comma) → "12.50", as the server
// reads it; ".5" → "0.5"; "38." → "38".
function normaliseMoney(v) {
  let s = v.replace(/[\s$]/g, '')
  s = !s.includes('.') && /^\d+,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '')
  if (s.startsWith('.')) s = `0${s}`
  if (s.endsWith('.')) s = s.slice(0, -1)
  return s
}

// clean() runs on every keystroke; normalise() turns what was typed into what
// is checked and sent. Fields with clean() are length-checked by check(), so a
// pasted "6104.63.0010" isn't cut short before its dots are removed.
const FIELDS = [
  {
    key: 'customs_description', label: 'Customs description', req: true, max: 50, width: 'min-w-[260px]',
    normalise: v => v.replace(/\s+/g, ' ').trim(),
    check: v => {
      if (v.length < 3) return 'Too short. Say what the item is and what it’s made of.'
      if (STOP_WORDS.has(v.toLowerCase().replace(/^[ .]+|[ .]+$/g, ''))) return 'Too vague for customs. Say what the item is and what it’s made of.'
      return null
    },
    tip: {
      title: 'Customs description',
      body: 'A plain description of what the item is, what it is made of and what it is used for. Customs uses it to check the HS code. Vague words on their own, like “gift”, “sample”, “parts”, “accessories” or “goods”, are rejected by EU customs (ICS2) and can hold parcels elsewhere.',
      fmt: [['Limit', '50 characters'], ['Example', 'Women’s leggings, knitted polyester/elastane']],
      ref: 'Ref: Australia Post (50-character description), EU ICS2 goods description rules, Zonos customs description',
    },
  },
  {
    key: 'declared_value', label: 'Declared value (AUD)', req: true, max: 10, width: 'min-w-[110px]', money: true,
    clean: v => v.replace(/[^0-9.,]/g, ''),
    normalise: normaliseMoney,
    check: v => {
      if (!/^\d+(\.\d+)?$/.test(v)) return 'Enter a number above 0 with up to 2 decimals, e.g. 38.00.'
      const n = parseFloat(v)
      if (!(n > 0)) return 'Declared value must be more than $0.'
      if (n > 9999999.99) return 'Declared value is too large.'
      if (!/^\d+(\.\d{1,2})?$/.test(v)) return 'Declared value can have at most 2 decimal places.'
      return null
    },
    tip: {
      title: 'Declared value (AUD)',
      body: 'The value of one unit in Australian dollars, set by the client. It will be used on customs declarations for international parcels, and as the basis for OPS lost-parcel cover where that’s part of your agreement. Undervaluing can cause penalties and delays at customs.',
      fmt: [['Format', 'Number, up to 2 decimals, AUD per unit'], ['Example', '38.00']],
      ref: 'Ref: Australia Post customs declaration (item value), Zonos customs value',
    },
  },
  {
    key: 'hs_code', label: 'HS code', req: true, max: 10, width: 'min-w-[110px]', mono: true, placeholder: '6–10 digits',
    clean: v => v.replace(/[\s.-]/g, ''),
    normalise: v => v.replace(/[\s.-]/g, ''),
    check: v => {
      if (/^\d+e\+?\d+$/i.test(v)) return 'Excel turned this HS code into a number. Type the code again.'
      if (!/^\d+$/.test(v)) return 'HS code can only contain numbers.'
      return v.length >= 6 && v.length <= 10 ? null : 'HS code must be 6 to 10 digits (US parcels need 10).'
    },
    tip: {
      title: 'HS code (Harmonized System)',
      body: 'The international customs code that classifies the product and decides duties and taxes. The first 6 digits are the same in every country, and each country adds up to 4 more for its own tariff. Parcels to the United States need the full 10-digit US code: Australia Post rejects US labels without one from July 2026.',
      fmt: [['Limit', '10 digits'], ['Format', '6–10 numbers. Dots, dashes and spaces are removed for you'], ['US parcels', 'Exactly 10 digits, from the US tariff (HTS)'], ['Example', '610463']],
      ref: 'Ref: Zonos HS code guide, Australia Post US guide (10-digit rule), World Customs Organization',
    },
  },
  {
    key: 'country_of_origin', label: 'Country of origin', req: true, max: 40, width: 'min-w-[170px]', country: true,
    check: v => COUNTRY_NAME[v] ? null : 'Pick a country from the list.',
    tip: {
      title: 'Country of origin',
      body: 'The country where the product was made or substantially transformed. This is not where it ships from or where the brand is based. It affects duty rates and trade-agreement eligibility. Type the country name (for example “China” or “Vietnam”) and pick it from the list. The 2-letter code customs needs fills in for you. Spreadsheet uploads accept country names too.',
      fmt: [['Stored as', '2-letter ISO code'], ['Example', 'China → CN, Vietnam → VN, Australia → AU']],
      ref: 'Ref: Zonos country of origin, Australia Post (country of manufacture), ISO 3166-1',
    },
  },
  {
    key: 'mid_code', label: 'MID code', max: 15, width: 'min-w-[160px]', mono: true,
    clean: v => v.toUpperCase().replace(/[\s-]/g, ''),
    normalise: v => v.toUpperCase().replace(/[\s-]/g, ''),
    check: v => /^[A-Z]{2}[A-Z0-9]{3,13}$/.test(v) ? null : 'MID starts with 2 letters, then letters and numbers (15 max).',
    tip: {
      title: 'MID code (Manufacturer Identification Code)',
      body: 'Needed by US Customs for parcels going to the United States. It identifies the factory that made the goods, not OPS or the brand. Built from the manufacturer’s country, name, street number and city.',
      fmt: [['Limit', '15 characters, no spaces'], ['Format', 'Letters and numbers, starting with the 2-letter country code'], ['Example', 'CNSHEPRO123SHE']],
      ref: 'Ref: US CBP rules (19 CFR Part 102 appendix), ShipStation MID field (US-bound only)',
    },
  },
  {
    key: 'certificate_version_id', label: 'Certificate version ID', max: 50, width: 'min-w-[150px]', mono: true,
    check: () => null,
    tip: {
      title: 'Certificate version ID',
      body: 'From the US Consumer Product Safety Commission (CPSC) Product Registry. Only needed for products that require a CPSC certificate (e.g. children’s products) going to the US. It identifies which version of the certificate applies.',
      fmt: [['Limit', '50 characters'], ['Where to find it', 'CPSC Product Registry']],
      ref: 'Ref: ShipStation CPSC eFiling (50-character limit), CPSC Product Registry',
    },
  },
  {
    key: 'certifier_id', label: 'Certifier ID', max: 50, width: 'min-w-[140px]', mono: true,
    check: () => null,
    tip: {
      title: 'Certifier ID',
      body: 'Also from the CPSC Product Registry. It identifies the business that certified the product. Entered together with the Certificate version ID for US eFiling.',
      fmt: [['Limit', '50 characters'], ['Where to find it', 'CPSC Product Registry']],
      ref: 'Ref: ShipStation CPSC eFiling (50-character limit), CPSC Product Registry',
    },
  },
]
const FIELD = Object.fromEntries(FIELDS.map(f => [f.key, f]))
const REQUIRED = FIELDS.filter(f => f.req).map(f => f.key)

const OTHER_TIPS = {
  sku: { title: 'SKU', body: 'The product code used in ShipStation and the online store. It links this product to every order line.', fmt: [['Example', 'LEG-HW-BLK-M']], ref: 'Ref: ShipStation product SKU' },
  status: { title: 'Status', body: 'Ready means the four required fields are filled in and in the right format, so the product has what a customs declaration needs.', fmt: [], ref: '' },
  source: { title: 'Source', body: 'Where the current values came from (ShipStation import, entered or uploaded by OPS, or entered or uploaded by the client), who changed them and when. History lists every change.', fmt: [], ref: '' },
}

const SOURCE_LABEL = {
  shipstation_import: 'ShipStation import',
  admin_entry: 'Entered by OPS',
  admin_upload: 'Uploaded by OPS',
  client_entry: 'Entered by client',
  client_upload: 'Uploaded by client',
}

// What's wrong with a value, or null. 'missing' = a required field is blank.
// typed=true for what someone just typed: placeholders are only rejected then.
function fieldProblem(f, raw, typed = false) {
  const t = asText(raw).trim()
  if (!t) return f.req ? 'missing' : null
  if (typed && isPlaceholder(t)) return PLACEHOLDER_MSG
  const v = f.normalise ? f.normalise(t) : t
  if (!v) return f.req ? 'missing' : null
  if (!f.clean && v.length > f.max) return `Limit is ${f.max} characters.`
  return f.check(v)
}

// The saved value as the cell shows it (declared value always with 2 decimals).
function storedText(f, p) {
  const v = p[f.key]
  if (f.money && v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v))) return Number(v).toFixed(2)
  return asText(v)
}

function localMissing(p) { return REQUIRED.filter(k => fieldProblem(FIELD[k], storedText(FIELD[k], p))) }
// The server decides completeness; the local rules are only a fallback.
function isDone(p) { return typeof p.complete === 'boolean' ? p.complete : localMissing(p).length === 0 }
function missingOf(p) { return Array.isArray(p.missing) ? p.missing.filter(k => REQUIRED.includes(k)) : localMissing(p) }

// ─── CSV ─────────────────────────────────────────────────────────────────────

// "Last changed" is each product's updated_at when the file was downloaded. It
// comes back on upload as `version`, so the server can keep a filled-in value
// that was changed in the portal after the download instead of quietly undoing
// it (it says so in the upload notes). A file without the column reads as before.
const CSV_COLUMNS = [
  ['sku', 'SKU'], ['product_name', 'Product name'], ['customs_description', 'Customs description'],
  ['declared_value', 'Declared value (AUD)'], ['hs_code', 'HS code'], ['country_of_origin', 'Country of origin'],
  ['mid_code', 'MID code'], ['certificate_version_id', 'Certificate version ID'], ['certifier_id', 'Certifier ID'],
  ['version', 'Last changed (do not edit)'],
]
const COLUMN_LABEL = Object.fromEntries(CSV_COLUMNS)
const HEADER_ALIASES = {
  sku: 'sku', 'product sku': 'sku', 'item sku': 'sku', 'variant sku': 'sku',
  'product name': 'product_name', name: 'product_name', product: 'product_name',
  'customs description': 'customs_description', description: 'customs_description',
  'declared value (aud)': 'declared_value', 'declared value aud': 'declared_value', 'declared value': 'declared_value', value: 'declared_value', 'customs value': 'declared_value',
  'hs code': 'hs_code', hs: 'hs_code', 'hs tariff code': 'hs_code', 'hs tariff number': 'hs_code', 'tariff code': 'hs_code',
  'customs tariff no': 'hs_code', 'customs tariff number': 'hs_code', 'harmonized code': 'hs_code', 'hts code': 'hs_code',
  'country of origin': 'country_of_origin', country: 'country_of_origin', origin: 'country_of_origin', 'country of manufacture': 'country_of_origin', 'customs country code': 'country_of_origin',
  'mid code': 'mid_code', mid: 'mid_code',
  'certificate version id': 'certificate_version_id', 'cpsc certificate version id': 'certificate_version_id',
  'certifier id': 'certifier_id', 'cpsc certifier id': 'certifier_id',
  'last changed (do not edit)': 'version', 'last changed': 'version',
}
// Written as Excel text formulas (="0901210000") so Excel, Google Sheets and
// Numbers keep leading zeros and don't turn barcodes into 9.31E+12, or SKUs
// like MAR-1 and the Last changed stamp into dates.
const TEXT_COLUMNS = new Set(['sku', 'hs_code', 'mid_code', 'certificate_version_id', 'certifier_id', 'version'])
// A cell starting with one of these runs as a formula in spreadsheet apps.
const FORMULA_START = /^[=+\-@\t\r]/
// A leading apostrophe (Excel's "keep as text" mark) is dropped from these on
// upload. Not from descriptions or names, where it can be real text ('90s tee).
const APOSTROPHE_COLUMNS = new Set(['sku', 'declared_value', 'hs_code', 'country_of_origin', 'mid_code', 'certificate_version_id', 'certifier_id', 'version'])
const EXCEL_NUMBER = /^\d(\.\d+)?e\+?\d+$/i
const MAX_UPLOAD_ROWS = 5000
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
// The longest Last changed stamp the server takes (ours are 32 characters).
const MAX_VERSION_LENGTH = 40

function csvCell(v) {
  const s = asText(v)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function excelText(s) { return `="${s.replace(/"/g, '""')}"` }

function downloadCell(key, p) {
  if (key === 'declared_value') return storedText(FIELD.declared_value, p)
  let s = asText(key === 'version' ? p.updated_at : p[key])
  if (!s) return ''
  // "NA" would come back as a placeholder on upload; the name round-trips.
  if (key === 'country_of_origin' && s === 'NA') s = 'Namibia'
  const risky = FORMULA_START.test(s)
  if ((TEXT_COLUMNS.has(key) || risky) && s.length <= 250) return csvCell(excelText(s))
  return csvCell(risky ? `'${s}` : s)
}

// ="0901210000" → 0901210000 (our own download writes codes that way).
function unwrapExcelText(s) {
  const m = /^\s*="((?:[^"]|"")*)"\s*$/.exec(s)
  return m ? m[1].replace(/""/g, '"') : s
}

function cellValue(key, raw) {
  let s = unwrapExcelText(asText(raw)).trim()
  if (APOSTROPHE_COLUMNS.has(key) && s.startsWith("'")) s = s.slice(1).trim()
  return s
}

function normHeader(h) {
  return unwrapExcelText(asText(h)).replace(/^﻿/, '').replace(/\s+/g, ' ').trim().replace(/\s*\*$/, '').toLowerCase()
}

// Excel on Windows saves "CSV (Comma delimited)" as windows-1252, not UTF-8.
async function readCsvFile(file) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
    throw problem('This is an Excel workbook, not a CSV file. In Excel choose File › Save As › CSV UTF-8, then upload that file.')
  }
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes)
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes)
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('windows-1252').decode(bytes)
  }
}

// European Excel saves "CSV" with semicolons; "Unicode text" uses tabs.
// Decided from the first line that has any separator in it (the header).
function detectDelimiter(text) {
  let quoted = false
  let counts = { ',': 0, ';': 0, '\t': 0 }
  let lines = 0
  for (let i = 0; i < text.length && lines < 10; i++) {
    const ch = text[i]
    if (quoted) { if (ch === '"') quoted = false; continue }
    if (ch === '"') quoted = true
    else if (ch in counts) counts[ch] += 1
    else if (ch === '\n' || ch === '\r') {
      if (counts[','] || counts[';'] || counts['\t']) break
      counts = { ',': 0, ';': 0, '\t': 0 }
      lines += 1
    }
  }
  if (!counts[','] && counts[';']) return ';'
  if (!counts[','] && !counts[';'] && counts['\t']) return '\t'
  return ','
}

// Declared values in an upload that the server would misread. "1.250,00" is
// only ever European (one thousand two hundred and fifty), so it's sent as
// 1250.00: the server reads a dot as the decimal point and would store $1.25.
// A semicolon or tab file can come from Excel set up for Europe, so there
// "1.250" and "1,250" could each mean 1250 or 1.25; those are reported, never
// guessed. Plain values like 38.00 or 38,50 go through unchanged.
// → { value } to send, or { error } (the cell is then left unchanged).
function uploadMoney(raw, delim) {
  let s = raw.replace(/\s+/g, '').replace(/\$/g, '')
  if (/^aud/i.test(s)) s = s.slice(3)
  if (/aud$/i.test(s)) s = s.slice(0, -3)
  if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(s)) return { value: s.replace(/\./g, '').replace(',', '.') }
  const unclear = { error: `“${raw}” can be read as two different amounts. Write declared values without a thousands separator and with at most 2 decimals (e.g. 1250.00 or 1250,00), then upload again.` }
  // A comma after a dot is European order, but not a valid amount (1.250,000).
  if (s.includes('.') && s.includes(',') && s.indexOf('.') < s.lastIndexOf(',')) return unclear
  if (delim !== ',') {
    if (/^\d{1,3}(\.\d{3})+$/.test(s)) return unclear                                    // 1.250
    if (!s.includes('.') && s.includes(',') && !/^\d+,\d{1,2}$/.test(s)) return unclear  // 1,250 or 0,125
  }
  return { value: raw }
}

// Records with their spreadsheet row number (1-based; blank rows still count,
// and a quoted cell with line breaks stays in one row, as in Excel).
function parseCsv(text, delim) {
  const records = []
  let row = [], cell = '', quoted = false, fieldStart = true, line = 1
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++ }
      else if (ch === '"') quoted = false
      else cell += ch
    } else if (ch === '"' && fieldStart) {
      quoted = true
      fieldStart = false
    } else if (ch === delim) {
      row.push(cell); cell = ''; fieldStart = true
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(cell); records.push({ line, cells: row })
      row = []; cell = ''; fieldStart = true; line += 1
    } else {
      cell += ch
      fieldStart = false
    }
  }
  if (cell !== '' || row.length) { row.push(cell); records.push({ line, cells: row }) }
  return records
}

// → { rows, ignored, problems }. rows carry `row`, their line in the file, so
// problems point at the right spreadsheet row. Blank cells are sent as "" (the
// server leaves those fields unchanged). problems are found here, before
// anything is sent, in the same shape as the server's.
function csvToRows(text) {
  const clean = text.replace(/^﻿/, '')
  const delim = detectDelimiter(clean)
  const records = parseCsv(clean, delim).filter(r => r.cells.some(c => c.trim() !== ''))
  if (!records.length) throw problem('The file is empty.')
  // The header is the first row with a SKU heading (normally row 1).
  const headAt = records.slice(0, 10).findIndex(r => r.cells.some(h => HEADER_ALIASES[normHeader(h)] === 'sku'))
  if (headAt < 0) throw problem('No SKU column found. Download the spreadsheet first and fill that in.')
  const head = records[headAt]
  const keys = head.cells.map(h => HEADER_ALIASES[normHeader(h)] || null)
  const firstHeading = {}
  keys.forEach((k, i) => {
    if (!k) return
    const heading = head.cells[i].trim()
    if (k in firstHeading) {
      throw problem(`Two columns are both read as ${COLUMN_LABEL[k]}: “${firstHeading[k]}” and “${heading}”. Delete or rename one of them, then upload again.`)
    }
    firstHeading[k] = heading
  })
  const ignored = head.cells.filter((h, i) => !keys[i] && h.trim()).map(h => h.trim())
  const rows = []
  const problems = []
  for (const r of records.slice(headAt + 1)) {
    const out = { row: r.line }
    keys.forEach((k, i) => { if (k) out[k] = cellValue(k, r.cells[i] ?? '') })
    // Longer than the server takes, so not a stamp from our download, and it
    // would fail the whole upload. Sent blank, the row is read like a file
    // without the column.
    if (out.version && out.version.length > MAX_VERSION_LENGTH) out.version = ''
    if (EXCEL_NUMBER.test(out.sku || '')) {
      // Never send it: an OPS upload would add "9.31E+12" as a new SKU.
      problems.push({ row: r.line, sku: out.sku, field: 'sku', message: 'Excel turned this SKU into a number. Format the SKU column as Text, type the SKU again and upload.' })
      continue
    }
    if (out.declared_value) {
      const money = uploadMoney(out.declared_value, delim)
      if (money.error) {
        // Like a server-side field problem: this value stays as it is and the
        // rest of the row is still saved.
        problems.push({ row: r.line, sku: out.sku || '', field: 'declared_value', message: money.error })
        out.declared_value = ''
      } else {
        out.declared_value = money.value
      }
    }
    rows.push(out)
  }
  return { rows, ignored, problems }
}

function uploadCounts(r, problems) {
  const issues = x => (Array.isArray(x) ? x.filter(i => i && typeof i === 'object') : [])
  const byRow = (a, b) => (Number(a.row) || 0) - (Number(b.row) || 0)
  return {
    created: Number(r?.created) || 0,
    updated: Number(r?.updated) || 0,
    unchanged: Number(r?.unchanged) || 0,
    errors: [...problems, ...issues(r?.errors)].sort(byRow),
    warnings: issues(r?.warnings).sort(byRow),
  }
}

function IssueList({ items }) {
  return (
    <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-lg">
      {items.map((e, i) => {
        const row = typeof e.row === 'number' || typeof e.row === 'string' ? e.row : ''
        const field = typeof e.field === 'string' && e.field !== 'sku' ? (COLUMN_LABEL[e.field] || e.field) : ''
        return (
          <li key={i} className="px-3 py-1.5 text-xs text-slate-600">
            Row {row || '?'}{typeof e.sku === 'string' && e.sku ? ` (${e.sku})` : ''}{field ? `, ${field}` : ''}: {typeof e.message === 'string' && e.message ? e.message : 'Problem with this row.'}
          </li>
        )
      })}
    </ul>
  )
}

// ─── Floating helpers (tooltip + country list) ────────────────────────────────

function placeBelow(rect, width, height) {
  const left = Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8))
  let top = rect.bottom + 6
  if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 6)
  return { left, top }
}

// Opens on hover, focus or click; a click never closes it. Closes on Escape,
// blur, mouse leave or scroll.
function HeaderTip({ tip, label }) {
  const btn = useRef(null)
  const box = useRef(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ left: 0, top: 0 })

  useEffect(() => {
    if (!open || !btn.current || !box.current) return
    setPos(placeBelow(btn.current.getBoundingClientRect(), box.current.offsetWidth, box.current.offsetHeight))
    const close = () => setOpen(false)
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', close, true)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={`What is ${label}?`}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400 hover:text-blue-600 focus:text-blue-600 focus:outline-none"
      >
        <Info size={13} />
      </button>
      {open && (
        <div
          ref={box}
          role="tooltip"
          style={{ left: pos.left, top: pos.top }}
          className="fixed z-50 w-80 max-w-[calc(100vw-16px)] bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-700 normal-case tracking-normal font-normal space-y-2 pointer-events-none"
        >
          <p className="font-semibold text-slate-800 text-[13px]">{tip.title}</p>
          <p className="leading-relaxed">{tip.body}</p>
          {tip.fmt.length > 0 && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {tip.fmt.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-mono text-[11px] text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
          )}
          {tip.ref && <p className="text-[11px] text-slate-400">{tip.ref}</p>}
        </div>
      )}
    </>
  )
}

// onCommit(code) saves and resolves to true/false; onCommit(null, message)
// reports a problem without saving.
function CountryInput({ value, onCommit, disabled, ariaLabel, problem: cellProblem }) {
  const input = useRef(null)
  const picked = useRef(false)      // a pick already saved; skip the blur check
  const valueRef = useRef(value || '')
  valueRef.current = value || ''
  const textRef = useRef(value || '')
  const [text, setTextState] = useState(value || '')
  const setText = (t) => { textRef.current = t; setTextState(t) }
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const [pos, setPos] = useState({ left: 0, top: 0 })
  // Only what you've typed is searched: an empty or untouched cell has no
  // suggestions, so Enter there can't pick one.
  const query = text.trim() && text.trim() !== (value || '') ? text : ''
  const matches = useMemo(() => countryMatches(query), [query])
  const naHint = !!query && isNamibiaPlaceholder(query)
  const listRows = query ? Math.max(1, matches.length + (naHint ? 1 : 0)) : 1

  useEffect(() => { setText(value || '') }, [value])

  useEffect(() => {
    if (!open || !input.current) return
    const r = input.current.getBoundingClientRect()
    const h = Math.min(260, 36 * listRows + 8)
    setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - 264)), top: r.bottom + h + 8 > window.innerHeight ? Math.max(8, r.top - h - 4) : r.bottom + 4 })
  }, [open, listRows])

  // Save a code; if the save fails, put the stored value back in the box.
  function commit(code) {
    Promise.resolve(onCommit(code)).then(ok => { if (ok === false) setText(valueRef.current) })
  }

  function pick(code) {
    picked.current = true
    setOpen(false)
    setText(code)
    if (code !== valueRef.current) commit(code)
  }

  function leave() {
    setTimeout(() => {
      setOpen(false)
      if (picked.current) { picked.current = false; return }
      const typed = textRef.current
      const current = valueRef.current
      if (typed.trim() === current) { setText(current); return }   // untouched
      const r = resolveTypedCountry(typed)
      if (r.error) { setText(current); onCommit(null, r.error); return }
      setText(r.code)
      if (r.code !== current) commit(r.code)
    }, 120)
  }

  const name = value ? COUNTRY_NAME[value] : ''
  return (
    <div>
      <input
        ref={input}
        value={text}
        disabled={disabled}
        maxLength={40}
        placeholder="Type a country"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        onFocus={e => { setOpen(true); setIndex(0); setTimeout(() => e.target.select(), 0) }}
        onChange={e => { setText(e.target.value); setOpen(true); setIndex(isNamibiaPlaceholder(e.target.value) ? -1 : 0) }}
        onBlur={leave}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setIndex(i => Math.min(i + 1, matches.length - 1)) }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setIndex(i => Math.max(i - 1, 0)) }
          else if (e.key === 'Enter') {
            e.preventDefault()
            if (open && query && index >= 0 && matches[index]) pick(matches[index].code)
            e.target.blur()
          } else if (e.key === 'Escape') { setText(value || ''); setOpen(false) }
        }}
        className={`w-full px-2 py-1.5 rounded-md text-sm font-mono border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          cellProblem === 'missing' ? 'bg-amber-50 border-transparent placeholder-amber-600' : cellProblem ? 'bg-amber-50 border-amber-400' : 'border-transparent hover:border-slate-200 bg-transparent'
        }`}
      />
      <div className={`px-2 text-[11px] truncate max-w-[180px] ${value && !name ? 'text-amber-600' : 'text-slate-500'}`}>
        {value ? (name || 'Not a recognised country') : 'Start typing a name'}
      </div>
      {open && !disabled && (
        <ul role="listbox" style={{ left: pos.left, top: pos.top }} className="fixed z-50 w-64 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg p-1">
          {!query && <li role="presentation" className="px-2 py-1.5 text-sm text-slate-500">Start typing a country</li>}
          {naHint && <li role="presentation" className="px-2 py-1.5 text-xs text-amber-700">{NAMIBIA_HINT}</li>}
          {query && matches.length === 0 && <li role="presentation" className="px-2 py-1.5 text-sm text-slate-500">No country matches. Try another spelling.</li>}
          {matches.map((c, i) => (
            <li
              key={c.code}
              role="option"
              aria-selected={i === index}
              onMouseDown={e => { e.preventDefault(); pick(c.code); input.current?.blur() }}
              className={`flex justify-between gap-3 px-2 py-1.5 rounded-md text-sm cursor-pointer ${i === index ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <span>{c.name}</span><span className="font-mono text-xs text-slate-400">{c.code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Cells ───────────────────────────────────────────────────────────────────

function Cell({ field, product, onSave, saving }) {
  const stored = storedText(field, product)
  const [draft, setDraft] = useState(stored)
  const [error, setError] = useState(null)
  useEffect(() => { setDraft(stored); setError(null) }, [stored])

  const label = `${field.label} for ${product.sku}`
  if (field.country) {
    return (
      <CountryInput
        value={stored}
        disabled={saving}
        ariaLabel={label}
        problem={fieldProblem(field, stored)}
        onCommit={(code, message) => (message ? onSave(null, message) : onSave({ [field.key]: code ? countryForApi(code) : null }))}
      />
    )
  }

  const typed = draft !== stored
  const problem = error || fieldProblem(field, draft, typed)
  const shown = field.normalise ? field.normalise(draft.trim()) : draft.trim()
  const usShort = field.key === 'hs_code' && !problem && /^\d{6,9}$/.test(shown)

  async function commit() {
    const raw = draft.trim()
    const v = raw && field.normalise ? field.normalise(raw) : raw
    const same = v === stored.trim() || (field.money && v !== '' && stored !== '' && Number(v) === Number(stored))
    if (same) { if (draft !== stored) setDraft(stored); setError(null); return }
    const local = v ? fieldProblem(field, raw, true) : null
    if (local && local !== 'missing') { setError(local); return }
    const ok = await onSave({ [field.key]: v === '' ? null : v })
    if (!ok) setDraft(stored)
  }

  return (
    <div>
      <div className="flex items-center gap-0.5">
        {field.money && <span className="text-slate-400 text-sm">$</span>}
        <input
          value={draft}
          maxLength={field.clean ? field.max + 15 : field.max}
          disabled={saving}
          placeholder={problem === 'missing' ? 'Required' : (field.placeholder || '')}
          inputMode={field.money ? 'decimal' : field.key === 'hs_code' ? 'numeric' : undefined}
          aria-label={label}
          aria-invalid={!!problem && problem !== 'missing'}
          title={problem && problem !== 'missing' ? problem : undefined}
          onChange={e => { setError(null); setDraft(field.clean ? field.clean(e.target.value) : e.target.value) }}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setDraft(stored); setError(null) } }}
          className={`w-full px-2 py-1.5 rounded-md text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 ${field.mono ? 'font-mono' : ''} ${field.money ? 'text-right tabular-nums' : ''} ${
            problem === 'missing' ? 'bg-amber-50 border-transparent placeholder-amber-600'
              : problem ? 'bg-amber-50 border-amber-400' : 'border-transparent hover:border-slate-200 bg-transparent'
          }`}
        />
      </div>
      {field.max >= 50 && draft.length > 0 && (
        <div className={`text-[10.5px] text-right pr-1 tabular-nums ${draft.length >= field.max ? 'text-amber-600' : 'text-slate-400'}`}>
          {draft.length}/{field.max}
        </div>
      )}
      {problem && problem !== 'missing' && <div className="text-[11px] text-amber-700 px-1 max-w-[260px]">{problem}</div>}
      {usShort && <div className="text-[11px] text-amber-600 px-1">US needs 10 digits</div>}
    </div>
  )
}

// A removed product's value: shown, never edited.
function ReadOnlyCell({ field, product }) {
  const v = storedText(field, product)
  if (!v) return <div className="px-2 pt-1.5 text-sm text-slate-300">—</div>
  if (field.country) {
    return (
      <div className="px-2 pt-1.5">
        <div className="font-mono text-sm">{v}</div>
        <div className="text-[11px] truncate max-w-[180px]">{COUNTRY_NAME[v] || ''}</div>
      </div>
    )
  }
  return (
    <div className={`px-2 pt-1.5 text-sm break-words ${field.mono ? 'font-mono' : ''} ${field.money ? 'text-right tabular-nums' : ''}`}>
      {field.money ? `$${v}` : v}
    </div>
  )
}

// ─── Change history (admin) ───────────────────────────────────────────────────

function historyValue(field, v) {
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return '(blank)'
  if (typeof v === 'object') return JSON.stringify(v)
  const s = String(v)
  if (field === 'declared_value' && Number.isFinite(Number(s))) return `$${Number(s).toFixed(2)}`
  if (field === 'country_of_origin' && COUNTRY_NAME[s]) return `${s} (${COUNTRY_NAME[s]})`
  return s
}

function HistoryRow({ change: c }) {
  const removal = c?.field === 'removed'
  const label = removal
    ? (asText(c.new_value).toLowerCase() === 'yes' ? 'Removed from list' : 'Put back on list')
    : (FIELD[c?.field]?.label || (typeof c?.field === 'string' ? c.field : 'Change'))
  const email = typeof c?.changed_by_email === 'string' ? c.changed_by_email : ''
  const role = c?.changed_by_role === 'admin' ? 'OPS' : c?.changed_by_role === 'client' ? 'client' : ''
  const who = email ? `${email}${role ? ` (${role})` : ''}` : (c?.source === 'shipstation_import' ? 'Automatic' : '')
  const source = typeof c?.source === 'string' && c.source ? (SOURCE_LABEL[c.source] || c.source) : ''
  const note = typeof c?.note === 'string' ? c.note : ''
  return (
    <li className="py-2.5 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="font-medium text-slate-800">{label}</span>
        <span className="text-xs text-slate-400 tabular-nums">{fmtDate(c?.changed_at, 'd MMM yyyy, h:mm a')}</span>
      </div>
      {!removal && (
        <div className="mt-0.5 text-slate-700 break-words">
          <span className="text-slate-500">{historyValue(c?.field, c?.old_value)}</span>
          <span className="text-slate-400 px-1.5">→</span>
          <span>{historyValue(c?.field, c?.new_value)}</span>
        </div>
      )}
      {(source || who || note) && <div className="mt-0.5 text-xs text-slate-500">{[source, who, note].filter(Boolean).join(' · ')}</div>}
    </li>
  )
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

// Drawn on document.body, not inside the tab: the tab's space-y spacing would
// push the overlay 16px down and leave the app header uncovered and clickable.
function HistoryDialog({ clientId, product, onClose }) {
  const dialog = useRef(null)
  const closeBtn = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const { data, isLoading, error } = useQuery({
    queryKey: ['product-history', clientId, product.id],
    queryFn: () => getProductHistory(clientId, product.id),
    staleTime: 0,
  })

  useEffect(() => {
    const before = document.activeElement
    closeBtn.current?.focus()
    const inside = el => !!dialog.current && dialog.current.contains(el)
    // Keyboard focus stays in the dialog: Tab and Shift+Tab cycle through its
    // controls instead of reaching the customs cells behind it, where typing
    // and leaving a cell would save it.
    const onKey = e => {
      if (e.key === 'Escape') { onCloseRef.current(); return }
      if (e.key !== 'Tab') return
      const els = [...(dialog.current?.querySelectorAll(FOCUSABLE) || [])].filter(el => !el.disabled)
      if (!els.length) { e.preventDefault(); return }
      const first = els[0], last = els[els.length - 1]
      const at = document.activeElement
      if (e.shiftKey && (at === first || !inside(at))) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && (at === last || !inside(at))) { e.preventDefault(); first.focus() }
    }
    // Anything else that moves focus behind the dialog brings it back.
    const onFocusIn = e => { if (!inside(e.target)) closeBtn.current?.focus() }
    window.addEventListener('keydown', onKey)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('focusin', onFocusIn)
      if (before && typeof before.focus === 'function' && document.contains(before)) before.focus()
    }
  }, [])

  const changes = Array.isArray(data?.changes) ? data.changes : []
  const outside = e => { if (e.target === e.currentTarget) onClose() }
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onMouseDown={outside}>
      <div className="flex min-h-full items-start justify-center p-4 py-8" onMouseDown={outside}>
        <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="product-history-title" className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
          <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-slate-200">
            <div>
              <h3 id="product-history-title" className="font-semibold text-slate-800">Change history</h3>
              <p className="text-xs text-slate-400 mt-0.5 font-mono">{product.sku}</p>
            </div>
            <button
              ref={closeBtn}
              type="button"
              onClick={onClose}
              aria-label="Close history"
              className="text-slate-400 hover:text-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <X size={18} />
            </button>
          </div>
          {/* Focusable so the list can be scrolled from the keyboard. */}
          <div tabIndex={0} role="region" aria-label="Changes" className="px-6 py-3 max-h-[70vh] overflow-y-auto rounded-b-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500">
            {isLoading ? (
              <div className="flex items-center gap-2 text-slate-400 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Loading history…</div>
            ) : error ? (
              <div className="flex gap-2 text-sm text-slate-600 py-4">
                <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                <span>{errorMessage(error, 'Couldn’t load the history.')}</span>
              </div>
            ) : changes.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No changes recorded yet.</p>
            ) : (
              <ol className="divide-y divide-slate-100">
                {changes.map((c, i) => <HistoryRow key={i} change={c} />)}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ─── Main tab ────────────────────────────────────────────────────────────────

export default function ProductsTab({ clientId, clientName, mode = 'admin', preview = false }) {
  // apiMode picks the endpoints and never changes; `admin` is how the tab is
  // drawn. "Preview client view" draws the client presentation over the admin
  // API, so edits made in the preview are recorded as OPS.
  const apiMode = mode === 'admin' ? 'admin' : 'client'
  const [previewClient, setPreviewClient] = useState(false)
  const admin = apiMode === 'admin' && !previewClient
  const qc = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const queryKey = useMemo(() => ['products', apiMode, clientId], [apiMode, clientId])

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getProducts(clientId, apiMode),
    enabled: !!clientId,
    // A new SKU arrives within about 5 minutes of its first order reaching
    // ShipStation; keep an open tab current.
    // (Only while the browser tab is visible.)
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  })

  const [filter, setFilter] = useState('todo')
  const [search, setSearch] = useState('')
  const [savingKeys, setSavingKeys] = useState(() => new Set())   // `${productId}:${field}`
  const [stickyIds, setStickyIds] = useState(() => new Set())
  const [uploadResult, setUploadResult] = useState(null)
  const [uploadNote, setUploadNote] = useState('')
  const [uploading, setUploading] = useState(false)
  const [confirmTicked, setConfirmTicked] = useState(false)
  const [removeId, setRemoveId] = useState(null)
  const [historyFor, setHistoryFor] = useState(null)
  const [viewEpoch, setViewEpoch] = useState(0)
  const fileInput = useRef(null)
  const saveChains = useRef(new Map())   // product id → its last save, so saves run in order
  const order = useRef(null)

  const products = useMemo(() => data?.products || [], [data])

  // Rows keep their place while you work: a background refresh puts new SKUs
  // at the top instead of re-sorting rows you've just edited. The server's
  // order (incomplete first) comes back when you change filter, upload or import.
  const ordered = useMemo(() => {
    if (!order.current || order.current.epoch !== viewEpoch || order.current.rank.size === 0) {
      order.current = { epoch: viewEpoch, rank: new Map(products.map((p, i) => [p.id, i])), top: 0 }
    }
    const o = order.current
    const fresh = products.filter(p => !o.rank.has(p.id))
    fresh.forEach((p, i) => o.rank.set(p.id, o.top - fresh.length + i))
    o.top -= fresh.length
    return [...products].sort((a, b) => o.rank.get(a.id) - o.rank.get(b.id))
  }, [products, viewEpoch])

  const activeProducts = useMemo(() => ordered.filter(p => !p.removed), [ordered])
  const removedProducts = useMemo(() => ordered.filter(p => p.removed), [ordered])
  const showRemoved = admin && filter === 'removed'

  // Progress, missing counts and the spreadsheet only count products on the list.
  const summary = useMemo(() => {
    const missing = Object.fromEntries(REQUIRED.map(k => [k, 0]))
    let complete = 0, shortHs = 0
    for (const p of activeProducts) {
      for (const k of missingOf(p)) missing[k] += 1
      if (isDone(p)) complete += 1
      const hs = asText(p.hs_code)
      if (hs && hs.length < 10) shortHs += 1
    }
    return { total: activeProducts.length, complete, missing, shortHs }
  }, [activeProducts])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    const base = showRemoved ? removedProducts : activeProducts
    return base.filter(p => {
      if (q && !`${p.sku} ${p.product_name || ''} ${p.customs_description || ''}`.toLowerCase().includes(q)) return false
      if (showRemoved || filter === 'all' || stickyIds.has(p.id)) return true
      return filter === 'done' ? isDone(p) : !isDone(p)
    })
  }, [activeProducts, removedProducts, showRemoved, filter, search, stickyIds])

  // Back to the server's order with no sticky rows. waitForData: a refetch is
  // on its way, so take the order from that instead of the current data.
  function resetView(waitForData = false) {
    setStickyIds(new Set())
    order.current = null
    if (!waitForData) setViewEpoch(n => n + 1)
  }

  function switchView(toClient) {
    setPreviewClient(toClient)
    setRemoveId(null)
    setHistoryFor(null)
    setConfirmTicked(false)
    if (toClient && filter === 'removed') setFilter('todo')
    resetView()
  }

  // Put a saved change into the cached list. A refresh already on its way may
  // have been read before the change and would put the old value back, so it's
  // cancelled; if there was one, it's started again, so whatever it was
  // bringing (new SKUs from an import, an upload's values) still arrives.
  async function updateCache(update) {
    const wasFetching = qc.isFetching({ queryKey }) > 0
    await qc.cancelQueries({ queryKey })
    qc.setQueryData(queryKey, old => old && update(old))
    if (wasFetching) qc.invalidateQueries({ queryKey })
  }

  function patchRow(row) {
    if (!row || typeof row !== 'object' || !row.id) return Promise.resolve()
    return updateCache(old => ({ ...old, products: old.products.map(p => p.id === row.id ? { ...p, ...row } : p) }))
  }

  async function saveField(product, fields, message) {
    if (message) { addToast({ type: 'error', message }); return false }
    if (!fields) return false
    const key = `${product.id}:${Object.keys(fields).join(',')}`
    setSavingKeys(s => new Set(s).add(key))
    setStickyIds(s => new Set(s).add(product.id))
    // Saves for one product run one after another, so an older response can
    // never overwrite a newer one. Only the cell being saved is disabled.
    const prev = saveChains.current.get(product.id) || Promise.resolve()
    const run = prev.then(async () => {
      const row = await updateProduct(clientId, product.id, fields, apiMode)
      await patchRow(row)
    })
    const settled = run.then(() => {}, () => {})
    saveChains.current.set(product.id, settled)
    try {
      await run
      return true
    } catch (e) {
      addToast({ type: 'error', message: errorMessage(e, 'Couldn’t save that change. Try again.') })
      return false
    } finally {
      if (saveChains.current.get(product.id) === settled) saveChains.current.delete(product.id)
      setSavingKeys(s => { const n = new Set(s); n.delete(key); return n })
    }
  }

  const importMut = useMutation({
    mutationFn: () => importProductsFromShipstation(clientId),
    onSuccess: r => {
      resetView(true)
      qc.invalidateQueries({ queryKey })
      const added = Number(r?.added) || 0
      const filled = Number(r?.filled_fields) || 0
      const shared = Number(r?.skipped_shared) || 0
      let msg = `Added ${added} new SKU${added === 1 ? '' : 's'} and filled ${filled} blank field${filled === 1 ? '' : 's'}. Nothing already entered was changed.`
      if (shared > 0) msg += ` ${shared} SKU${shared === 1 ? '' : 's'} used by more than one client ${shared === 1 ? 'was' : 'were'} not filled from ShipStation.`
      const failed = Math.max(0, (Array.isArray(r?.errors) ? r.errors.length : 0) - (r?.shipstation_error ? 1 : 0))
      if (failed > 0) msg += ` ${failed} SKU${failed === 1 ? '' : 's'} couldn’t be saved. Try again in a minute.`
      if (r?.shipstation_error) {
        const why = typeof r.shipstation_error === 'string' ? r.shipstation_error : 'unknown error'
        addToast({ type: 'warning', message: `${msg} ShipStation couldn’t be checked (${why}), so only SKUs from order history were added.`, duration: 10000 })
      } else if (!r?.shipstation_checked) {
        addToast({ type: 'warning', message: `${msg} ShipStation isn’t set up, so only SKUs from order history were added.`, duration: 8000 })
      } else {
        addToast({ type: failed ? 'warning' : 'success', message: msg, duration: failed ? 8000 : undefined })
      }
    },
    onError: e => addToast({ type: 'error', message: errorMessage(e, 'Import failed. Try again in a minute.') }),
  })

  const accessMut = useMutation({
    mutationFn: v => setProductsClientAccess(clientId, v),
    onSuccess: async r => {
      await updateCache(old => ({ ...old, client_visible: r.client_visible }))
      addToast({ type: 'success', message: r.client_visible ? `${clientName || 'The client'} can now see Products in their portal.` : 'Products hidden from the client again.' })
    },
    onError: e => addToast({ type: 'error', message: errorMessage(e, 'Couldn’t change client access.') }),
  })

  const confirmMut = useMutation({
    mutationFn: () => confirmProducts(clientId, apiMode),
    onSuccess: async r => {
      setConfirmTicked(false)
      await updateCache(old => ({ ...old, last_confirmation: r }))
      addToast({ type: 'success', message: 'Values confirmed.' })
    },
    onError: e => addToast({ type: 'error', message: errorMessage(e, 'Couldn’t record the confirmation.') }),
  })

  // "Remove" keeps the row (ignored_at) so the 5-minute product sync doesn't
  // add the SKU straight back; it moves to the Removed filter.
  const removeMut = useMutation({
    mutationFn: p => deleteProduct(clientId, p.id),
    onSuccess: async (_r, p) => {
      setRemoveId(null)
      await updateCache(old => ({
        ...old,
        products: old.products.map(x => x.id === p.id ? { ...x, removed: true, ignored_at: x.ignored_at || new Date().toISOString() } : x),
      }))
      setStickyIds(s => { const n = new Set(s); n.delete(p.id); return n })
      addToast({ type: 'success', message: `${p.sku} is off the list. You can restore it from the Removed filter.` })
    },
    onError: e => addToast({ type: 'error', message: errorMessage(e, 'Couldn’t remove that SKU.') }),
  })

  const restoreMut = useMutation({
    mutationFn: p => restoreProduct(clientId, p.id),
    onSuccess: async (row, p) => {
      await updateCache(old => ({
        ...old,
        products: old.products.map(x => x.id === p.id
          ? { ...x, ...(row && typeof row === 'object' ? row : {}), removed: false, ignored_at: null, ignored_by_email: null }
          : x),
      }))
      addToast({ type: 'success', message: `${p.sku} is back on the list.` })
    },
    onError: e => addToast({ type: 'error', message: errorMessage(e, 'Couldn’t restore that SKU.') }),
  })

  async function onFile(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || uploading) return
    setUploading(true)
    setUploadResult(null)
    let sent = false
    try {
      if (file.size > MAX_UPLOAD_BYTES) throw problem(`That file is too big. Upload at most ${MAX_UPLOAD_ROWS.toLocaleString()} products at a time.`)
      const { rows, ignored, problems } = csvToRows(await readCsvFile(file))
      if (rows.length > MAX_UPLOAD_ROWS) {
        throw problem(`The file has ${rows.length.toLocaleString()} product rows. Upload at most ${MAX_UPLOAD_ROWS.toLocaleString()} at a time.`)
      }
      if (!rows.length && !problems.length) throw problem('No product rows found under the header row.')
      const note = admin ? uploadNote.trim() : ''
      let r = {}
      if (rows.length) {
        sent = true
        r = await uploadProducts(clientId, rows, note || null, apiMode)
      }
      setUploadResult({ file: file.name, showCreated: apiMode === 'admin', ignored, ...uploadCounts(r, problems) })
      if (sent && admin) setUploadNote('')
      resetView(true)
      qc.invalidateQueries({ queryKey })
    } catch (err) {
      qc.invalidateQueries({ queryKey })
      setUploadResult({ file: file.name, fatal: errorMessage(err, 'Upload failed. Try again.'), sent })
    } finally {
      setUploading(false)
    }
  }

  function download() {
    const lines = [CSV_COLUMNS.map(([, h]) => csvCell(h)).join(',')]
    for (const p of activeProducts) lines.push(CSV_COLUMNS.map(([k]) => downloadCell(k, p)).join(','))
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(clientName || 'products').replace(/[^\w-]+/g, '_')}_products_${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  if (isLoading) {
    return <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 size={16} className="animate-spin" /> Loading products…</div>
  }
  if (error && !data) {
    return (
      <div className="card p-6 text-sm text-slate-600 flex gap-2">
        <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
        <span>{errorMessage(error, 'Couldn’t load products.')}</span>
      </div>
    )
  }

  // Rounded down, so 100% only ever means every product is ready (199 of 200
  // is 99%). Whole numbers first: 0.57 * 100 is 56.99999999999999 in JS.
  const pct = summary.total ? Math.floor((summary.complete * 100) / summary.total) : 0
  const missingLabels = { customs_description: 'description', declared_value: 'declared value', hs_code: 'HS code', country_of_origin: 'country of origin' }
  const last = data?.last_confirmation
  const lastBy = last && (previewClient && last.confirmed_by_role === 'admin' ? 'OPS 3PL' : last.confirmed_by_email)
  const colCount = FIELDS.length + (admin ? 4 : 2)
  const removedCount = removedProducts.length
  const incompleteCount = summary.total - summary.complete
  const searching = search.trim() !== ''
  const filters = [['todo', 'Needs details'], ['done', 'Complete'], ['all', 'All']]
  if (admin && (removedCount > 0 || filter === 'removed')) filters.push(['removed', `Removed (${removedCount})`])

  let emptyText = 'No products match.'
  if (showRemoved && removedCount === 0) emptyText = 'No removed products.'
  else if (!showRemoved && activeProducts.length === 0) {
    emptyText = admin
      ? 'No products yet. A product is added automatically within about 5 minutes of its first order reaching ShipStation, so ones nobody has ordered yet won’t show. Import from ShipStation runs the full check for this client now instead of waiting for the daily run.'
      : 'No products yet. OPS will add them for you.'
  } else if (searching) emptyText = 'No products match your search.'
  else if (filter === 'todo' && incompleteCount === 0) emptyText = 'Nothing left to fill in. Every product has its customs details.'
  else if (filter === 'done') emptyText = 'No products are complete yet.'

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {admin && (
        <div className="flex flex-wrap gap-2 items-start rounded-lg bg-violet-50 text-violet-800 px-3 py-2.5 text-sm">
          <Lock size={15} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[240px]"><strong className="font-semibold">Only visible to your account.</strong> Other OPS admins can’t see this tab. {data?.client_visible ? 'The client can see and edit it in their portal.' : 'The client can’t see it until you turn on client access.'}</div>
          <button type="button" onClick={() => switchView(true)} className="inline-flex items-center gap-1.5 font-medium text-violet-700 hover:text-violet-900 hover:underline shrink-0">
            <Eye size={14} /> Preview client view
          </button>
        </div>
      )}
      {previewClient && (
        <div className="flex flex-wrap gap-2 items-start rounded-lg bg-amber-50 text-amber-800 px-3 py-2.5 text-sm">
          <Eye size={15} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <strong className="font-semibold">Preview.</strong> This is what {clientName || 'the client'} sees in their portal{data?.client_visible ? '.' : ' once you turn on client access. Right now they can’t see this tab.'} Anything you change here is saved for real and recorded as OPS.
          </div>
          <button type="button" onClick={() => switchView(false)} className="inline-flex items-center gap-1.5 font-medium text-amber-800 hover:text-amber-950 hover:underline shrink-0">
            <ArrowLeft size={14} /> Back to admin view
          </button>
        </div>
      )}
      {apiMode === 'client' && preview && (
        <div className="flex gap-2 items-start rounded-lg bg-amber-50 text-amber-800 px-3 py-2.5 text-sm">
          <Eye size={15} className="mt-0.5 shrink-0" />
          <div><strong className="font-semibold">Preview.</strong> This is what the client will see once you turn on client access. Right now they can’t see this tab.</div>
        </div>
      )}
      {error && data && (
        <p className="text-xs text-amber-700">Couldn’t refresh the product list just now. What you see may be a few minutes old.</p>
      )}

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <div className="card p-4 space-y-3 min-w-0">
          <p className="text-sm font-medium text-slate-500">Customs details complete</p>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl font-semibold text-slate-800 tabular-nums">{pct}%</span>
            <span className="text-sm text-slate-500">{summary.complete} of {summary.total} products ready</span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap gap-2">
            {REQUIRED.filter(k => summary.missing[k]).map(k => (
              <span key={k} className="badge bg-amber-50 text-amber-700">{summary.missing[k]} missing {missingLabels[k]}</span>
            ))}
            {summary.shortHs > 0 && <span className="badge bg-amber-50 text-amber-700">{summary.shortHs} need a 10-digit HS code to ship to the US</span>}
            {summary.total > 0 && summary.complete === summary.total && <span className="badge bg-emerald-50 text-emerald-700">Every product is complete</span>}
          </div>
        </div>

        {admin ? (
          <div className="card p-4 space-y-3 min-w-0">
            <p className="text-sm font-medium text-slate-500">Client access</p>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">Show Products to client</p>
                <p className="text-xs text-slate-500">{data?.client_visible ? 'On. They can see and edit their products.' : 'Off. Only you can see and edit these products.'}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!data?.client_visible}
                aria-label="Show Products to client"
                disabled={accessMut.isPending}
                onClick={() => accessMut.mutate(!data?.client_visible)}
                className={`relative w-10 h-6 rounded-full shrink-0 transition-colors ${data?.client_visible ? 'bg-blue-600' : 'bg-slate-300'} disabled:opacity-50`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${data?.client_visible ? 'left-5' : 'left-1'}`} />
              </button>
            </div>
            <p className="text-xs text-slate-500">
              {last ? <>Last confirmed {fmtDate(last.confirmed_at)} by {last.confirmed_by_email} ({last.products_complete}/{last.products_total} complete).</> : 'The client hasn’t confirmed their values yet.'}
            </p>
          </div>
        ) : (
          <div className="card p-4 space-y-2 min-w-0">
            <p className="text-sm font-medium text-slate-500">Why we need this</p>
            <p className="text-sm text-slate-700 leading-relaxed">These details will be used for customs declarations on international parcels, and the declared value will be the basis for OPS lost-parcel cover where that’s part of your agreement. Please keep them accurate. You’re responsible for the values you declare.</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {filters.map(([k, l]) => (
            <button
              key={k}
              type="button"
              aria-pressed={filter === k}
              onClick={() => { setFilter(k); setRemoveId(null); resetView() }}
              className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              {l}
            </button>
          ))}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SKU or product" aria-label="Search SKU or product" className="input pl-8 py-1.5 w-56" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {admin && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => importMut.mutate()}
              disabled={importMut.isPending}
              title="Runs the full ShipStation check for this client now instead of waiting for the daily run. New products are added automatically within about 5 minutes of their first order reaching ShipStation; products nobody has ordered yet won’t show."
            >
              {importMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Import from ShipStation
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={() => fileInput.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? 'Uploading…' : 'Upload spreadsheet'}
          </button>
          <button type="button" className="btn-secondary" onClick={download} disabled={!activeProducts.length}><Download size={14} /> Download spreadsheet</button>
          <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
        </div>
      </div>
      {admin && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <label htmlFor="upload-note">Note for the next upload (optional):</label>
          <input id="upload-note" value={uploadNote} onChange={e => setUploadNote(e.target.value)} maxLength={200} placeholder="e.g. Emailed by client 3 Nov" className="input py-1 text-xs w-64" />
        </div>
      )}

      {uploadResult && (
        <div className="card p-4 text-sm space-y-2">
          <div className="flex items-start justify-between gap-3">
            {uploadResult.fatal ? (
              <p className="text-slate-700 flex gap-2">
                <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                <span><strong className="font-semibold">{uploadResult.file}:</strong> {uploadResult.fatal}</span>
              </p>
            ) : (
              <p className="text-slate-700">
                <strong className="font-semibold">{uploadResult.file}:</strong>{' '}
                {uploadResult.showCreated && <>{uploadResult.created} added, </>}{uploadResult.updated} updated, {uploadResult.unchanged} unchanged
                {uploadResult.errors.length ? `, ${uploadResult.errors.length} problem${uploadResult.errors.length === 1 ? '' : 's'}.` : '.'}
              </p>
            )}
            <button type="button" onClick={() => setUploadResult(null)} aria-label="Close upload result" className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          {uploadResult.fatal ? (
            <p className="text-xs text-slate-500">
              {uploadResult.sent
                ? 'The upload may not have finished, so some rows could already be saved. The table has been refreshed; check it before you try again.'
                : 'Nothing was uploaded.'}
            </p>
          ) : (
            <>
              {uploadResult.errors.length > 0 && <IssueList items={uploadResult.errors} />}
              {uploadResult.warnings.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-600">Notes</p>
                  <IssueList items={uploadResult.warnings} />
                </div>
              )}
              {uploadResult.ignored.length > 0 && (
                <p className="text-xs text-slate-600">Ignored columns: {uploadResult.ignored.join(', ')}. Only the columns in the downloaded spreadsheet are read.</p>
              )}
              <p className="text-xs text-slate-400">Blank cells leave the saved value unchanged.</p>
            </>
          )}
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2.5 font-semibold"><span className="inline-flex items-center gap-1">SKU <HeaderTip tip={OTHER_TIPS.sku} label="SKU" /></span></th>
              {FIELDS.map(f => (
                <th key={f.key} className="px-3 py-2.5 font-semibold whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">{f.label}{f.req && <span className="text-amber-600">*</span>} <HeaderTip tip={f.tip} label={f.label} /></span>
                </th>
              ))}
              <th className="px-3 py-2.5 font-semibold"><span className="inline-flex items-center gap-1">Status <HeaderTip tip={OTHER_TIPS.status} label="Status" /></span></th>
              {admin && <th className="px-3 py-2.5 font-semibold"><span className="inline-flex items-center gap-1">Source <HeaderTip tip={OTHER_TIPS.source} label="Source" /></span></th>}
              {admin && <th className="px-3 py-2.5" aria-label="Actions" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-3 py-10 text-center text-slate-500">{emptyText}</td>
              </tr>
            )}
            {visible.map(p => {
              const removed = !!p.removed
              const done = !removed && isDone(p)
              const restoring = restoreMut.isPending && restoreMut.variables?.id === p.id
              return (
                <tr key={p.id} className={`align-top ${removed ? 'bg-slate-50 text-slate-400' : ''}`}>
                  <td className="px-3 py-2">
                    <div className={`font-mono text-xs whitespace-nowrap pt-1.5 ${removed ? 'text-slate-500' : 'text-slate-800'}`}>{p.sku}</div>
                    {p.product_name && <div className="text-[11px] text-slate-400 max-w-[180px] truncate" title={p.product_name}>{p.product_name}</div>}
                  </td>
                  {FIELDS.map(f => (
                    <td key={f.key} className={`px-1.5 py-1.5 ${f.width}`}>
                      {removed
                        ? <ReadOnlyCell field={f} product={p} />
                        : <Cell field={f} product={p} saving={savingKeys.has(`${p.id}:${f.key}`)} onSave={(fields, message) => saveField(p, fields, message)} />}
                    </td>
                  ))}
                  <td className="px-3 py-2 pt-3">
                    {removed ? (
                      <>
                        <span className="badge bg-slate-100 text-slate-600 whitespace-nowrap">Removed</span>
                        <div className="text-[11px] text-slate-400 mt-1 whitespace-nowrap">
                          {fmtDate(p.ignored_at)}{p.ignored_by_email ? ` · ${p.ignored_by_email}` : ''}
                        </div>
                      </>
                    ) : done
                      ? <span className="badge bg-emerald-50 text-emerald-700 whitespace-nowrap"><CheckCircle2 size={12} className="mr-1" />Ready</span>
                      : <span className="badge bg-amber-50 text-amber-700 whitespace-nowrap">Needs details</span>}
                  </td>
                  {admin && (
                    <td className="px-3 py-2 pt-2.5 text-xs text-slate-500 whitespace-nowrap">
                      {p.value_source ? SOURCE_LABEL[p.value_source] || p.value_source : 'Not set'}
                      <div className="text-[11px] text-slate-400">
                        {fmtDate(p.updated_at)}{p.updated_by_email ? ` · ${p.updated_by_email}` : ''}
                      </div>
                      {p.source_note && <div className="text-[11px] text-slate-400 max-w-[180px] truncate" title={p.source_note}>{p.source_note}</div>}
                      <button
                        type="button"
                        onClick={() => setHistoryFor({ id: p.id, sku: p.sku })}
                        className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
                      >
                        <HistoryIcon size={11} /> History
                      </button>
                    </td>
                  )}
                  {admin && (
                    <td className="px-2 py-2 pt-2.5 whitespace-nowrap">
                      {removed ? (
                        <button
                          type="button"
                          onClick={() => restoreMut.mutate(p)}
                          disabled={restoring}
                          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline disabled:opacity-50"
                        >
                          {restoring ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restore
                        </button>
                      ) : removeId === p.id ? (
                        <div className="w-56 whitespace-normal text-xs text-slate-600 space-y-1.5">
                          <p>Take <span className="font-mono">{p.sku}</span> off the list? It won’t be added back from orders. You can restore it from the Removed filter.</p>
                          <div className="flex items-center gap-3">
                            <button type="button" className="text-red-600 font-medium hover:underline disabled:opacity-50" disabled={removeMut.isPending} onClick={() => removeMut.mutate(p)}>
                              {removeMut.isPending ? 'Removing…' : 'Remove from list'}
                            </button>
                            <button type="button" className="text-slate-500 hover:underline" onClick={() => setRemoveId(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Remove ${p.sku} from list`}
                          title="Remove from list (e.g. an insert or a discontinued product)"
                          onClick={() => setRemoveId(p.id)}
                          className="text-slate-300 hover:text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400">
        Hover or tap the <Info size={11} className="inline -mt-0.5" /> next to a heading to see what it means and the format it needs. Required fields are marked *. MID and certificate IDs are only needed for parcels to the United States. Character limits use the tightest of Australia Post, ShipStation and US Customs, so nothing will be cut off on a label.
      </p>

      <div className="card p-4 space-y-3">
        <label className="flex items-start gap-2.5 text-sm text-slate-700">
          <input type="checkbox" checked={confirmTicked} disabled={previewClient} onChange={e => setConfirmTicked(e.target.checked)} className="mt-0.5 w-4 h-4 accent-blue-600" />
          <span>{admin ? 'Record that the client has confirmed (e.g. by email): ' : ''}{data?.confirmation_statement}</span>
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn-primary" disabled={previewClient || !confirmTicked || confirmMut.isPending} onClick={() => confirmMut.mutate()}>
            {confirmMut.isPending && <Loader2 size={14} className="animate-spin" />} Confirm values
          </button>
          {previewClient && <span className="text-xs text-slate-500">Preview only. The client ticks the box and confirms here; nothing is recorded from the preview.</span>}
          {last && (
            <span className="text-sm text-emerald-700">
              Last confirmed {fmtDate(last.confirmed_at)}{lastBy ? ` by ${lastBy}` : ''}.
            </span>
          )}
        </div>
      </div>

      {historyFor && admin && <HistoryDialog clientId={clientId} product={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  )
}
